// /**
//  * Orders - list
//  */

// React
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

// Context
import AuthContext from '../../authContext';

// UI
import theme from '../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Checkbox,
  CircularProgress,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiClose,
  mdiFileDocumentOutline,
  mdiChevronDown,
  mdiChevronUp,
} from '@mdi/js';

// Components
import Can from '../../components/Can';

// Helper
import { translate, checkMatchMedia } from '../../utils/helpers';

// Type
import { Order, Status } from '../orders/interfaces';

type Data = Order;
type Props = {
  data: any;
  cancelOrderDispatch: any;
  bundleId: number;
  loading: boolean;
  selectedItems: number[];
  handleClickItem: (id: number) => void;
  selectAll: () => void;
};
type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
  onSelectAllClick: () => void;
  numSelected: number
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      fontSize: 14,
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };
  let headCells: HeadCell[] = [];

  Can({
    role: authContext.user.role,
    perform: 'orders:list:centerName',
    yes: () => {
      headCells = [
        {
          id: 'customerName',
          label: 'نام سفارش‌دهنده',
        },
        { id: 'id', label: 'شناسه سفارش' },
        { id: 'partnerName', label: 'نام شرکت' },
        { id: 'centerName', label: 'مرکز سفارش' },
        { id: 'status', label: 'وضعیت سفارش' },
      ];
    },
    no: () => {
      headCells = [
        {
          id: 'status',
          label: 'وضعیت بررسی'
        },
        {
          id: 'customerName',
          label: 'نام سفارش‌دهنده',
        },
        { id: 'id', label: 'شناسه سفارش' },
        { id: 'trackingCode', label: 'کد پیگیری' },
        { id: 'status', label: 'وضعیت سفارش' },
      ];
    },
  });

  return (
    <TableHead>
      <StyledTableRow>
        <StyledTableCell>
          <Checkbox
            indeterminate={
              props.numSelected > 0 && props.numSelected < props.rowCount
            }
            checked={props.rowCount > 0 && props.numSelected === props.rowCount}
            onChange={props.onSelectAllClick}
            inputProps={{ 'aria-label': 'select all desserts' }}
            disableRipple={true}
            style={{ color: '#fff' }}
          />
        </StyledTableCell>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  // const [dense] = React.useState(false);
  const [openChangeStatusConfirm, setOpenChangeStatusConfirm] = React.useState(
    false
  );
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);
  const [cancelDescription, setCancelDescription] = useState('' as string);
  const [description, setDescription] = useState<string>('')

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const changeStatusOrder = () => {
    props.updateOrderStatusDispatch({
      id,
      body: {
        status: previewState,
        description: (previewState === 'CANCELLED' || previewState === 'NOT_DELIVERED' || previewState === 'RETURNED') ? description : undefined
      },
      pagination: { 'bundleId.equals': props.bundleId },
    });
    setOpenChangeStatusConfirm(false);
    setDescription('');
  };

  const handleOpenConfirmCancel = (id: number) => {
    setOpenConfirmCancel(true);
    setCancelDescription('');
    setId(id);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: Status }) => {
    setOpenChangeStatusConfirm(true);
    setId(data.id);
    setPreviewState(data.body);
  };
  const findOrderStatus = (id: number) => {
    const status = props.data.find((order: Order) => order.id === id)?.status;
    return status && translate(status, 'order');
  };

  const handleCancelOrder = () => {
    props.cancelOrderDispatch({
      id,
      body: { description: cancelDescription, status: 'CANCELED' },
    });
    setOpenConfirmCancel(false);
  };
  const onClose = () => {
    setOpenConfirmCancel(false);
  };

  const handleOnchangeDescription = (event: { target: HTMLInputElement }) => {
    setDescription(event.target.value)
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        {props.loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer className={classes.container}>
            <Table
              stickyHeader
              className={classes.table}
              aria-labelledby="tableTitle"
              size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                onSelectAllClick={props.selectAll}
                rowCount={props.data.length}
                numSelected={props.selectedItems.length}
              />

              <TableBody>
                {stableSort(props.data, getComparator(order, orderBy))
                  .slice()
                  .map((row: any, index: number) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    let isChecked = !!props.selectedItems.find(el => el === row.id)
                    return (
                      <StyledTableRow
                        key={index}
                        hover
                        // onClick={(event) => handleClick(event, row.id)}
                        // role="checkbox"
                        tabIndex={-1}
                      >
                        <StyledTableCell >
                          <Checkbox
                            checked={isChecked}
                            key={index}
                            onClick={() => props.handleClickItem(row.id)}
                            inputProps={{ 'aria-labelledby': labelId }}
                          />
                          <span style={{ color: `${isChecked ? 'green' : 'red'}` }}>{isChecked ? 'بررسی شده' : 'بررسی نشده'}</span>
                        </StyledTableCell>
                        <TableCell
                          // component="th"
                          id={labelId}
                          scope="row"
                          align="center"
                        >
                          {row.customerName}
                        </TableCell>
                        <TableCell align="center" data-cy="row-id">
                          {row.id}
                        </TableCell>
                        <Can
                          role={authContext.user.role}
                          perform="orders:list:centerName"
                          yes={() => (
                            <TableCell align="center">
                              {row.centerName}
                            </TableCell>
                          )}
                          no={() => (
                            <TableCell align="center">
                              {row.trackingCode}
                            </TableCell>
                          )}
                        />
                        <TableCell align="center">
                          <span>{translate(row.status, 'order')}</span>
                        </TableCell>

                        <TableCell align="right">
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Link to={`/orders/${row.id}`}>
                              <IconButton
                                aria-label="details"
                                size="small"
                                title="جزئیات سفارش"
                                data-cy="details"
                              >
                                <Icon
                                  path={mdiFileDocumentOutline}
                                  size={1}
                                  color={theme.palette.green.main}
                                />
                              </IconButton>
                            </Link>

                            {/* <IconButton
                              aria-label="cancel"
                              title={
                                row.status === 'CANCELLED'
                                  ? 'لغو شده'
                                  : 'لغو سفارش'
                              }
                              size="small"
                              style={{ pointerEvents: 'fill' }}
                              disabled={row.status === 'CANCELLED'}
                              onClick={() => handleOpenConfirmCancel(row.id)}
                            >
                              <Icon
                                path={mdiClose}
                                size={1}
                                color={theme.palette.error.main}
                              />
                            </IconButton> */}
                          </div>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Dialog
        fullWidth
        open={openChangeStatusConfirm}
        onClose={() => setOpenChangeStatusConfirm(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>{`از تغییر تگ وضعیت سفارش
        "${id > -1 && findOrderStatus(id)}"
        به
       " ${translate(previewState.replace(/"/g, '') as Status, 'order')}"
        مطمئن هستید؟`}</DialogTitle>
        {(previewState === 'CANCELLED' || previewState === 'NOT_DELIVERED' || previewState === 'RETURNED') && (
          <DialogContent>
            <TextField
              className={classes.textArea}
              placeholder={`علت لغو...`}
              value={description}
              onChange={handleOnchangeDescription}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button
            autoFocus
            onClick={() => setOpenChangeStatusConfirm(false)}
            variant="contained"
            color="primary"
          >
            {`خیر`}
          </Button>
          <Button
            onClick={changeStatusOrder}
            variant="outlined"
            color="primary"
            disabled={((previewState === 'CANCELLED' || previewState === 'NOT_DELIVERED' || previewState === 'RETURNED') && description?.length === 0)}
          >
            {`بله`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        open={openConfirmCancel}
        onClose={onClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogContent>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {onClose ? (
              <IconButton aria-label="close" onClick={onClose}>
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div className={classes.cancelIcon}>
            <Icon path={mdiClose} size={4} color={theme.palette.error.main} />
          </div>
          <div
            className={classes.cancelTitle}
          >{`از لغو سفارش با شناسه ${id} مطمئن هستید؟`}</div>
          <TextField
            className={classes.textArea}
            placeholder={`علت لغو...`}
            value={cancelDescription}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCancelDescription(e.target.value)
            }
          />
        </DialogContent>
        <DialogActions className={classes.actionsBox}>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={() => setOpenConfirmCancel(false)}
                variant="contained"
                color="secondary"
                size="large"
              >
                {`خیر`}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                onClick={handleCancelOrder}
                variant="contained"
                size="large"
                disabled={!(cancelDescription.length > 0)}
              >
                {`بله`}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
}
